import { Formik } from 'formik';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, ModalCard, Radio } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import {
  calculateExpenseMarkup,
  calculateExpenseRevenue,
  calculateMarkupRatio,
  dateFormats,
  emptyStringToNull,
  ordinal,
} from '~/utils';

const ContainerControl = styled.span`
  display: flex;
  align-items: center;
`;

const Checkboxes = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    margin-right: 1.35rem;
  }
`;

export default function AddMultipleDialog({ project, currency, onSaved, onCancel }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();

  const initialValues = {
    start: null,
    end: null,
    dayOfMonth: null,
    amount: '',
    expenseCategory: null,
    isBillable: false,
    notes: '',
    vendor: '',
    enableMarkup: false,
    markupMethod: 'percentage',
    markupAmount: '',
    markupRatio: '',
  };

  const toast = useToast();
  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);

    if (!values.isBillable || !values.enableMarkup) {
      values.markupMethod = null;
      values.markupRatio = null;
      values.markupAmount = null;
    }
    switch (values.markupMethod) {
      case 'percentage':
        values.markupAmount = null;
        break;
      case 'amount':
        values.markupRatio = null;
        break;
    }

    const {
      start,
      end,
      dayOfMonth,
      amount,
      expenseCategory,
      isBillable,
      notes,
      vendor,
      markupMethod,
      markupAmount,
      markupRatio,
    } = emptyStringToNull(values);

    const { data: dates } = await api.www
      .workspaces(workspace.id)
      .projects(project.id)
      .generateRecurringDates({ start, end, dayOfMonth });

    const instances = dates.map((date) => {
      return emptyStringToNull({
        date: moment(date).format(dateFormats.isoDate),
        amount,
        expenseCategoryId: expenseCategory?.id,
        isBillable,
        notes,
        vendor,
        markupMethod,
        markupAmount,
        markupRatio: calculateMarkupRatio({
          enableMarkup: values.enableMarkup,
          markupRatio,
        }),
      });
    });

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).projectExpenses().batch.create(instances);
      toast.success('Project Expenses added successfully.');
      await onSaved();
    } catch (error) {
      toast.error('Failed to add project expenses.');
    }

    formik.setSubmitting(false);
  };

  return (
    <ModalCard title="Add Multiple Project Expenses" onClose={onCancel}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          start: Yup.date().label('Starting Month').nullable().required(),
          end: Yup.date()
            .label('Ending Month')
            .nullable()
            .min(Yup.ref('start'), 'Ending Month must be after Starting Month')
            .required(),
          dayOfMonth: Yup.mixed()
            .label('Day of Month')
            .oneOf([...Array(28).keys()].map((i) => i + 1).concat('last'))
            .required(),
          amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
          expenseCategory: Yup.object().label('Expense Category').nullable().required(),
          notes: Yup.string().label('Notes').max(255),
          vendor: Yup.string().label('Vendor').max(255),
          enableMarkup: Yup.bool(),
          markupMethod: Yup.string().label('Type'),
          markupAmount: Yup.number().label('Markup Amount').min(0).max(99999999999).nullable(),
          markupRatio: Yup.number().label('Markup Percentage').min(0).max(100).nullable(),
        })}>
        {(formik) => {
          let markup = calculateExpenseMarkup({
            ...formik.values,
            amount: formik.values.amount,
          });
          const totalToBill = calculateExpenseRevenue({
            markup: markup,
            amount: formik.values.amount,
          });

          const handleExpensesAmountChange = (value) => {
            // Deactivate "enableMarkup" if amount is 0 or null
            if (!value) formik.setFieldValue('enableMarkup', false);
            formik.setFieldValue('amount', value);
          };

          const handleIsBillableChange = ({ target: { value } }) => {
            // Deactivate "enableMarkup" if expense is non billable
            const newValue = !(value === 'true');
            if (!newValue) formik.setFieldValue('enableMarkup', false);
            formik.setFieldValue('isBillable', newValue);
          };

          return (
            <Form>
              <ModalCard.Body>
                <Form.Control>
                  <Field.DayPicker
                    name="start"
                    placeholder="Starting Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                  <Field.DayPicker
                    name="end"
                    placeholder="Ending Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                </Form.Control>

                <Form.Control>
                  <ContainerControl style={{ marginTop: '0' }}>
                    Scheduled on the
                    <div style={{ margin: '0 0.5rem' }}>
                      <Field.SingleSelect
                        name="dayOfMonth"
                        style={{
                          width: '7rem',
                          margin: '0 0.5rem',
                          textAlign: 'right',
                        }}
                        materialPlaceholder={false}>
                        {[...Array(28).keys()].map((day) => (
                          <option key={day} value={day + 1}>
                            {ordinal(day + 1)}
                          </option>
                        ))}
                        <option value="last">Last</option>
                      </Field.SingleSelect>
                    </div>
                    <span>day of each month</span>
                  </ContainerControl>
                </Form.Control>

                <Form.Control>
                  <Field.ExpenseCategorySelect name="expenseCategory" placeholder="Expense Category" allowNew />
                </Form.Control>

                <Form.Control>
                  <Checkboxes>
                    <Field.Checkbox
                      name="isBillable"
                      label="Bill to client"
                      disabled={!project.isBillable}
                      onChange={handleIsBillableChange}
                    />

                    {features.expenseMarkup && (
                      <Field.Checkbox
                        name="enableMarkup"
                        label="Markup Expense"
                        disabled={!formik.values.isBillable || !(formik.values.amount > 0)}
                      />
                    )}
                  </Checkboxes>
                </Form.Control>

                <Form.Control>
                  <Field.Text name="vendor" placeholder="Vendor" maxLength={255} />
                </Form.Control>

                <Form.Control>
                  <Field.Currency
                    name="amount"
                    placeholder="Amount"
                    currency={currency}
                    onChange={handleExpensesAmountChange}
                  />
                </Form.Control>

                {formik.values.enableMarkup && formik.values.isBillable && (
                  <>
                    <Form.Control>
                      <Field.RadioGroup name="markupMethod">
                        <Radio key="percentage" value="percentage" label="Markup by Percentage" />
                        <Radio key="amount" value="amount" label="Markup by Fixed Amount" />
                      </Field.RadioGroup>
                    </Form.Control>
                    {formik.values.markupMethod && (
                      <Form.Control>
                        {formik.values.markupMethod === 'amount' && (
                          <Field.Currency currency={currency} name="markupAmount" placeholder="Markup Amount" />
                        )}

                        {formik.values.markupMethod === 'percentage' && (
                          <Field.Number
                            prefix="%"
                            name="markupRatio"
                            placeholder="Markup Percentage"
                            min={0}
                            max={100}
                            precision={2}
                          />
                        )}
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.Currency
                        name="expenseRevenue"
                        value={totalToBill}
                        materialPlaceholder="Expense Revenue"
                        disabled
                        materialAlwaysVisible
                        currency={currency}
                      />
                    </Form.Control>
                  </>
                )}

                <Form.Control>
                  <Field.Text name="notes" placeholder="Notes" maxLength={255} />
                </Form.Control>
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onCancel}>Close</CancelButton>
                  <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                    Add
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}
